import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { TrendingUp, TrendingDown, Repeat, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../config/api';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

const DetailsPortefeuilleDialog = ({ open, onClose, portefeuille }) => {
  const [historique, setHistorique] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && portefeuille) {
      loadHistorique();
    }
  }, [open, portefeuille]);

  const loadHistorique = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/portefeuilles/${portefeuille.id}/historique`,
        { withCredentials: true }
      );
      setHistorique(response.data.historique);
    } catch (error) {
      console.error('Error loading historique:', error);
      toast.error('Erreur lors du chargement de l\'historique');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'depot':
        return 'Dépôt manuel';
      case 'depot_recurrent':
        return 'Dépôt récurrent';
      case 'depense':
        return 'Dépense';
      default:
        return type;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'depot':
      case 'depot_recurrent':
        return <TrendingUp className="h-5 w-5 text-teal-600" />;
      case 'depense':
        return <TrendingDown className="h-5 w-5 text-amber-600" />;
      default:
        return <FileText className="h-5 w-5 text-gray-600" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'depot':
      case 'depot_recurrent':
        return 'border-teal-200 bg-teal-50';
      case 'depense':
        return 'border-amber-200 bg-amber-50';
      default:
        return 'border-gray-200 bg-gray-50';
    }
  };

  if (!portefeuille) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-blue-900 flex items-center gap-2">
            <FileText className="h-6 w-6" />
            Détails : {portefeuille.nom}
          </DialogTitle>
          <div className="text-sm text-gray-600 mt-2">
            Solde actuel : <span className="font-bold text-teal-700">{formatCurrency(portefeuille.solde)}</span>
          </div>
        </DialogHeader>

        <div className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-600">Chargement...</div>
            </div>
          ) : historique.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Aucune opération pour le moment</p>
            </div>
          ) : (
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-2">
                {historique.map((item) => (
                  <div
                    key={item.id}
                    className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-semibold text-sm text-gray-900">
                            {getTypeLabel(item.type)}
                          </span>
                          {item.type === 'depot_recurrent' && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                              <Repeat className="h-3 w-3" />
                              Automatique
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-2 text-xs text-gray-600 mb-1">
                          <span>{item.compte === 'personal' ? 'Personnel' : 'Partagé'}</span>
                          <span>•</span>
                          <span>{formatDate(item.date)}</span>
                        </div>

                        {item.description && item.description !== `Dépôt initial dans la portefeuille '${portefeuille.nom}'` && item.description !== `Dépôt manuel dans '${portefeuille.nom}'` && item.description !== `Versement récurrent - ${portefeuille.nom}` && (
                          <p className="text-xs text-gray-700 mt-1">
                            {item.description}
                          </p>
                        )}
                      </div>

                      <div className="text-right flex-shrink-0">
                        <div className={`text-lg font-bold ${
                          item.type === 'depense' ? 'text-amber-600' : 'text-teal-600'
                        }`}>
                          {item.type === 'depense' ? '-' : '+'}{formatCurrency(item.montant)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button onClick={onClose} variant="outline">
            Fermer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DetailsPortefeuilleDialog;
